import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  chakra,
  Divider,
  Heading,
  keyframes,
  Link,
  ListItem,
  OrderedList,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../../../components/Header/Header';
import { useGetRegistration } from '../../../../services/hooks/Registration/useGetRegistration';
import CardHeaderMobile from '../../../../components/Mobile/header';
import LabelWithValueMobilePage from '../../../../components/Mobile/labelWithValue';
import ProofRegistrationButton from '../../../../components/PDFs/DownloadPDFButton/proofRegistrationButton';
import TitleSectionMobilePage from '../../../../components/Mobile/titleSection';
import { FiArrowRightCircle } from 'react-icons/fi';
import InvoicePDFButton from '../../../../components/PDFs/DownloadPDFButton/invoiceButton';
import { badgeColor } from '../../../../utils/badgeFunction';
import RegistrationDocumentsSummary from '../RegistrationDocumentsSummary';
import { useRequestGetDataToRegistration } from '../../../../services/hooks/CandidateArea/useGetDataToRegistration';
import {
  getRegistrationExemptions,
  useRequestMyExemption,
} from '../../../../services/hooks/CandidateArea/useRequestMyExemption';
import RegistrationExemptionsSummary from '../RegistrationExemptionsSummary';

const floatIn = keyframes`
      from { opacity: 0; transform: translateY(18px) scale(0.995); }  
  `;

const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

const SummaryRegistrationMobilePage: React.FC = () => {
  const [params] = useSearchParams();
  const standbyExemption = params.get('standbyExemption');
  const previousRoute = params.get('previous-route');

  const border = useColorModeValue('green.300', 'green.600');
  const bg = useColorModeValue('green.50', 'green.900');
  const textColor = useColorModeValue('gray.700', 'gray.200');

  const selectiveProcessId = useLocation().pathname.split('/')[2];
  const registrationId = useLocation().pathname.split('/')[4];
  const navigate = useNavigate();

  const bgGradient = useColorModeValue('linear(to-br, gray.50, white)', 'linear(to-br, gray.900, gray.800)');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const getRegistration = useGetRegistration(registrationId || '');
  const selectiveProcessData = useRequestGetDataToRegistration(selectiveProcessId || '');
  const candidateExemptions = useRequestMyExemption();
  const sentExemptions = getRegistrationExemptions(
    candidateExemptions.data,
    getRegistration.data?.registration_code,
    selectiveProcessId,
  );

  const is_exemption = getRegistration?.data?.exemptions || [];

  const title = standbyExemption
    ? 'RESUMO DA INSCRIÇÃO'
    : is_exemption?.length > 0
      ? 'RESUMO DO PEDIDO DE ISENÇÃO'
      : 'RESUMO DA INSCRIÇÃO';

  const candidate = getRegistration.data?.candidate;

  return (
    <Stack h="100dvh" minH={0} w="100%" top={0} bg="white" gap={0} overflow="hidden">
      <Header />
      <VStack
        mt={0}
        flex={1}
        minH={0}
        w="100%"
        overflowX="hidden"
        overflowY="auto"
        overscrollBehavior="contain"
        py={2}
        pb={6}
      >
        {(getRegistration.isLoading && (
          <VStack flex={1} w="100%" justify="center">
            <Spinner emptyColor="gray.100" color="blue.500" size="xl" />
            <Text fontSize="sm">Buscando resumo do processo seletivo...</Text>
          </VStack>
        )) ||
          (getRegistration.isError && <Text>Erro ao trazer os dados.</Text>) || (
            <AnimatedBox
              bgGradient={bgGradient}
              borderRadius="lg"
              boxShadow="lg"
              mx={3}
              p={{ base: 5, md: 10 }}
              borderWidth={'1px'}
              borderColor={borderColor}
              bg="white"
              flex="0 0 auto"
              display="flex"
              w="calc(100% - 24px)"
              minW={0}
            >
              <VStack boxSizing="border-box" w="100%" minW={0} maxW="lg" spacing={4} align="stretch">
                <CardHeaderMobile
                  title={title}
                  subTitle="Tudo certo! Veja abaixo o resumo da sua inscrição. Confira os dados e, caso encontre alguma informação incorreta, você poderá corrigi-la até o prazo estipulado no edital. Se estiver tudo correto, é possível imprimir o seu comprovante."
                  colorScheme="blue"
                />
                <VStack spacing={2} align="stretch" minW={0}>
                  {previousRoute === '/painel/inscricoes' ? (
                    <></>
                  ) : (
                    <Alert
                      status="success"
                      variant="subtle"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      textAlign="center"
                      rounded="md"
                      mb={2}
                      p={5}
                    >
                      <AlertIcon boxSize="40px" mr={0} mb={2} />
                      {standbyExemption ? (
                        <Text as="span" fontWeight="semibold">
                          Parabéns! Sua inscrição foi realizada com sucesso e seu número de inscrição é{' '}
                          <strong>{getRegistration.data?.registration_code || '--'}</strong>.
                        </Text>
                      ) : is_exemption?.length > 0 ? (
                        <Text as="span" fontWeight="semibold" fontSize="0.95rem">
                          Parabéns! Seu pedido de isenção foi realizada com sucesso.
                        </Text>
                      ) : (
                        <Text as="span" fontWeight="semibold">
                          Parabéns! Sua inscrição foi realizada com sucesso e seu número de inscrição é{' '}
                          <strong>{getRegistration.data?.registration_code || '--'}</strong>.
                        </Text>
                      )}
                    </Alert>
                  )}
                  <TitleSectionMobilePage>Dados da solicitação</TitleSectionMobilePage>
                  <LabelWithValueMobilePage label="Inscrição" value={getRegistration.data?.registration_code} />
                  <LabelWithValueMobilePage label="Situação" value={getRegistration.data?.registration_status.name} />
                  <LabelWithValueMobilePage
                    label="Processo seletivo"
                    value={getRegistration.data?.selective_process_vacancy?.selective_process?.name}
                  />
                  {getRegistration.data?.selective_process_vacancy?.name && (
                    <LabelWithValueMobilePage
                      label={
                        getRegistration.data?.selective_process_vacancy?.cultural_house
                          ? getRegistration.data?.selective_process_vacancy?.class_level
                            ? 'Turma '
                            : 'Turma - Primeira opção: '
                          : 'Vaga: '
                      }
                      value={getRegistration.data?.selective_process_vacancy.name}
                    />
                  )}
                  {getRegistration.data?.second_vacancy?.name && (
                    <>
                      <LabelWithValueMobilePage
                        label="Turma - Segunda opção"
                        value={getRegistration.data?.second_vacancy?.name}
                      />
                    </>
                  )}
                  <LabelWithValueMobilePage label="Nome completo" value={candidate?.user.full_name} />
                  {candidate?.social_name && (
                    <LabelWithValueMobilePage label="Nome social" value={candidate?.social_name} />
                  )}
                  <LabelWithValueMobilePage
                    label={candidate?.is_foreign ? 'Passaporte' : 'CPF'}
                    value={candidate?.is_foreign ? candidate.passport_number : candidate?.cpf}
                  />
                  {getRegistration.data?.invoice?.status_invoice && (
                    <LabelWithValueMobilePage
                      label="Situação do boleto"
                      value={getRegistration.data?.invoice?.status_invoice}
                    />
                  )}
                  {selectiveProcessData.data?.allow_filter_vacancy_tree && (
                    <RegistrationDocumentsSummary registrationId={registrationId || ''} isMobile />
                  )}
                  {getRegistration.data?.special_conditions && getRegistration.data?.special_conditions?.length > 0 && (
                    <Stack mt={4}>
                      <TitleSectionMobilePage>Atendimento especial</TitleSectionMobilePage>
                      {getRegistration.data?.special_conditions.map((specialCondition, index) => (
                        <Box mt={0} key={index} bgColor="gray.50" p={2} borderRadius="md" pl={5}>
                          <LabelWithValueMobilePage label={specialCondition.name} value={specialCondition.status} />
                          <Text>
                            <strong>Observação:</strong> {specialCondition.observations}
                          </Text>
                          <Text fontWeight="semibold">Anexos:</Text>
                          <OrderedList pl={2} spacing={1}>
                            {specialCondition.attachments?.map((specialCondition, idx) => (
                              <Link href={specialCondition.url} color="colorLink" isExternal key={specialCondition.id}>
                                <ListItem listStyleType="none" fontSize="13px">
                                  Anexo {idx + 1}
                                </ListItem>
                              </Link>
                            ))}
                          </OrderedList>
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {getRegistration.data?.exemptions && getRegistration.data?.exemptions?.length > 0 && (
                    <Stack mt={4}>
                      <TitleSectionMobilePage>Isenção</TitleSectionMobilePage>
                      {getRegistration.data?.exemptions.map((exemption, index) => (
                        <Box key={index}>
                          <LabelWithValueMobilePage
                            label={exemption.type}
                            value={
                              <Badge
                                ml={2}
                                fontSize="xs"
                                color={badgeColor(exemption?.status || '')?.color || ''}
                                background={badgeColor(exemption?.status || '')?.bgColor || ''}
                              >
                                {exemption.status}
                              </Badge>
                            }
                          />
                        </Box>
                      ))}
                    </Stack>
                  )}
                  <RegistrationExemptionsSummary exemptions={sentExemptions} />
                  {!getRegistration?.data?.registration_status?.name.toLowerCase().includes('aguardando') ? (
                    <></>
                  ) : (
                    <Stack gap={0}>
                      <TitleSectionMobilePage>Pagamento</TitleSectionMobilePage>
                      {getRegistration.data?.candidate.is_foreign ? (
                        <Alert variant="top-accent" status="info">
                          <AlertDescription
                            px={2}
                            dangerouslySetInnerHTML={{
                              __html:
                                getRegistration.data?.selective_process_vacancy?.selective_process
                                  ?.information_foreign_payment ||
                                'No momento não foi adicionado nenhuma informação para pagamento.',
                            }}
                          ></AlertDescription>
                        </Alert>
                      ) : (
                        <Card border="1px solid" borderColor={border} borderRadius="lg" bg={bg} shadow="sm">
                          <CardHeader p={3}>
                            <Heading size="sm" color="green.600">
                              ⚠️&nbsp; Atenção
                            </Heading>
                          </CardHeader>

                          <Divider borderColor={border} />

                          <CardBody pb={1} color={textColor}>
                            <Text mb={3}>
                              Sua inscrição somente será efetivada após a confirmação do pagamento da taxa de inscrição.
                            </Text>

                            <Text fontWeight="medium">
                              <strong>Valor da inscrição:</strong> R${' '}
                              {getRegistration.data?.selective_process_vacancy?.value || '--'}
                            </Text>
                          </CardBody>

                          <CardFooter pt={0} mx="auto" w="100%">
                            <InvoicePDFButton registrationId={registrationId} />
                          </CardFooter>
                        </Card>
                      )}
                    </Stack>
                  )}
                  <Stack mt={4} w={['100%', '100%', 'fit-content', 'fit-content', 'fit-content']}>
                    {/* {getRegistration?.data?.show_print_registration_button && (
                      <Button
                        p={2}
                        fontSize="12px"
                        color="white"
                        bg="primaryColor"
                        whiteSpace="break-spaces"
                        h="fit-content"
                        onClick={handleImprimir}
                        leftIcon={<IoMdPrint size={23} />}
                        justifyContent="left"
                      >
                        {(isLoadFilePrint && <Spinner />) || standbyExemption
                          ? 'IMPRIMIR COMPROVANTE DE INSCRIÇÃO'
                          : getRegistration?.data?.exemptions && getRegistration?.data?.exemptions?.length > 0
                            ? 'IMPRIMIR COMPROVANTE DE ISENÇÃO'
                            : 'IMPRIMIR COMPROVANTE DE INSCRIÇÃO'}
                      </Button>
                    )} */}

                    <ProofRegistrationButton data={getRegistration.data} />

                    {getRegistration?.data?.show_view_registration_button && (
                      <ProofRegistrationButton data={getRegistration.data} isModeViewer />
                    )}
                    <Button
                      p={3}
                      fontSize="12px"
                      color="white"
                      bg="primaryColor"
                      whiteSpace="break-spaces"
                      leftIcon={<FiArrowRightCircle size={21} />}
                      h="fit-content"
                      onClick={() => navigate('/painel/inscricoes')}
                      justifyContent="left"
                    >
                      IR PARA MINHAS INSCRIÇÕES
                    </Button>
                  </Stack>
                </VStack>
              </VStack>
            </AnimatedBox>
          )}
      </VStack>
    </Stack>
  );
};

export default SummaryRegistrationMobilePage;
